/**
 * <Button> — canonical button.
 *
 * 8 variants × 3 sizes per docs/design-system/06-buttons-forms.md:
 *
 *   primary       — main action on a page / modal (one per surface)
 *   secondary     — supporting action (Cancel, secondary CTAs)
 *   ghost         — tertiary action (toolbar buttons, table row actions)
 *   outline       — emphasis without primary weight (boundary CTAs)
 *   destructive   — irreversible action; visually clear, not dramatic
 *   success       — positive non-primary affirmations (Approve, Mark complete)
 *   link          — text-only inline action that should look like a link
 *   icon          — icon-only square button; always pass `aria-label`
 *
 * Sizes:
 *   sm — 32px tall (dense toolbars, table rows)
 *   md — 36px tall (default)
 *   lg — 40px tall (page-level CTAs, modal primary)
 *
 * Touch target: every size bumps to 44px (`icon` variant: 44x44) under a
 * coarse pointer (`@media (pointer: coarse)`) — P1 touch-target audit
 * (2026-07-15), same coarse-pointer scoping <Input> already uses (D-3).
 * No-op on mouse/trackpad, so desktop density is unchanged.
 *
 * Loading state: width is preserved via an invisible label and an
 * absolutely-positioned spinner. The button cannot reflow when async work
 * starts. (Resolves the layout-shift bug that ad-hoc spinners often cause.)
 *
 * Resolves Phase-1 finding C5 (51% Button-primitive adoption — the
 * remaining 49% migrates as part of Phase 13 page-by-page sweep).
 *
 * Backwards compatibility:
 *   The legacy variant names (`danger`, `default`, `info`) used by
 *   `frontend/src/components/ui/Button.tsx` are accepted and silently
 *   mapped to the canonical names. The legacy size `xs` maps to `sm`;
 *   legacy `icon` maps to size `sm` + variant `icon`.
 *
 * Spec: docs/design-system/06-buttons-forms.md.
 */

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../utils/cn';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'outline'
  | 'destructive'
  | 'success'
  | 'link'
  | 'icon';

export type ButtonSize = 'sm' | 'md' | 'lg';

/** Variant names accepted at the API surface — includes legacy aliases. */
export type ButtonVariantInput =
  | ButtonVariant
  | 'danger'
  | 'default'
  | 'info';

/** Size names accepted at the API surface — includes legacy aliases. */
export type ButtonSizeInput = ButtonSize | 'xs' | 'icon';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariantInput;
  size?: ButtonSizeInput;
  /** Render full-width within parent container. */
  fullWidth?: boolean;
  /** Show spinner; preserves width. Always pair with disabled-on-pending logic at the call site. */
  isLoading?: boolean;
  /** Loading-state announcement for screen readers. Default 'Loading'. */
  loadingLabel?: string;
  /** Lucide icon (or anything renderable) before the label. */
  leftIcon?: ReactNode;
  /** Lucide icon (or anything renderable) after the label. */
  rightIcon?: ReactNode;
  children?: ReactNode;
}

const BASE_CLASSES =
  'relative inline-flex items-center justify-center gap-2 font-medium rounded-md transition-all duration-150 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-1 ' +
  'disabled:cursor-not-allowed disabled:opacity-50 select-none whitespace-nowrap';

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  // primary dark-mode boundary (WCAG 1.4.11, docs/governance/FOLLOW-UPS.md
  // "Dark `primary`-as-fill still short of 3:1"): the dark primary fill
  // #278384 is 2.91:1 vs --color-surface and 2.23:1 vs --color-elevated —
  // proven unfixable by re-toning while keeping white button text >=4.5:1
  // (luminance-budget proof in tokens/global.css .dark). The ratified fix is
  // boundary contrast instead of fill contrast: a dark-only 1px border in
  // --color-primary-hover (#6FB5B5, teal-300) — 5.59:1 vs surface, 4.28:1 vs
  // elevated, 7.11:1 vs bg (all >=3:1; pinned in
  // tokens/__tests__/contrast.test.ts). A border (not `ring`) so the
  // focus-visible ring is untouched and layout is unchanged (the transparent
  // border already reserves the pixel). On dark hover the fill itself becomes
  // #6FB5B5, which carries the boundary (5.59:1 vs surface). Light mode is
  // untouched (white on #0D4F4F and the fill vs the white surface: 9.34:1).
  primary:
    'bg-primary text-on-primary hover:bg-primary-hover shadow-sm border border-transparent ' +
    'dark:border-primary-hover',
  secondary:
    'bg-surface text-text border border-border hover:bg-elevated hover:border-border-strong',
  ghost:
    'bg-transparent text-muted hover:bg-elevated hover:text-text border border-transparent',
  outline:
    'bg-transparent text-primary border border-primary hover:bg-primary/8',
  // hover:bg-danger-hover / hover:bg-success-hover (real, per-palette fill
  // tokens — tokens/global.css) — not hover:bg-danger/success-muted
  // (`*-muted` is a text-on-tint color, LIGHTER than the base in dark mode,
  // so on-primary/white text over it is 1.74-2.02:1, worse than a no-op
  // hover), not an alpha idiom like hover:bg-danger/90 (backdrop-dependent —
  // Button renders on every surface in the app — measured failing 4.5:1
  // against several real dark surfaces), and NOT a CSS `brightness()` filter
  // applied on hover (a filter rasterizes the whole subtree, darkening the
  // on-primary/white LABEL along with the fill — measured 4.28:1 light /
  // 3.43:1 dark for danger, 4.38/3.04 for success once the text darkens
  // too, actually worse than the no-op it replaced). The *-hover tokens are
  // real darkened FILL
  // colors, so on-primary/white text against them holds regardless of
  // filters or backdrop — verified >=4.5:1 in both palettes (danger: 6.29
  // light / 5.09 dark; success: 7.67 light / 6.04 dark).
  //
  // The REST-state fill is bg-danger-fill / bg-success-fill, not bg-danger /
  // bg-success: the base tokens double as TEXT colors on dark surfaces and
  // are only 3.76:1 / 3.30:1 under white there (FOLLOW-UPS 2026-09-29). The
  // *-fill tokens are the same value in light mode and a hue-preserving
  // darker re-tone in dark mode (4.66:1 / 4.78:1 with on-primary) — see
  // tokens/global.css .dark and tokens/__tests__/contrast.test.ts.
  destructive:
    'bg-danger-fill text-on-primary hover:bg-danger-hover shadow-sm border border-transparent',
  success:
    'bg-success-fill text-on-primary hover:bg-success-hover shadow-sm border border-transparent',
  link:
    'bg-transparent text-primary hover:text-primary-hover underline-offset-4 hover:underline border border-transparent ' +
    'focus-visible:ring-primary/30 px-1',
  icon:
    'bg-transparent text-muted hover:bg-elevated hover:text-text border border-transparent',
};

// P1 touch-target audit (2026-07-15) — every declared size (32/36/40px) is
// under the 44px protocol bar. Mirrors the coarse-pointer scoping <Input>
// already uses for the same finding class (D-3, docs/audits/MOBILE_AUDIT.md
// §Decisions): the 44px floor only wins under `(pointer: coarse)`
// (touchscreens), so dense desktop toolbars/table rows keep their existing
// compact sizes on mouse/trackpad.
const COARSE_POINTER_TOUCH_TARGET = '[@media(pointer:coarse)]:h-11';
const COARSE_POINTER_ICON_TOUCH_TARGET = '[@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11';

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: `h-8 px-3 text-sm gap-1.5 ${COARSE_POINTER_TOUCH_TARGET}`,
  md: `h-9 px-4 text-sm gap-2 ${COARSE_POINTER_TOUCH_TARGET}`,
  lg: `h-10 px-5 text-md gap-2 ${COARSE_POINTER_TOUCH_TARGET}`,
};

const ICON_SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: `h-8 w-8 p-0 ${COARSE_POINTER_ICON_TOUCH_TARGET}`,
  md: `h-9 w-9 p-0 ${COARSE_POINTER_ICON_TOUCH_TARGET}`,
  lg: `h-10 w-10 p-0 ${COARSE_POINTER_ICON_TOUCH_TARGET}`,
};

const LINK_SIZE_OVERRIDE: Record<ButtonSize, string> = {
  sm: 'h-auto py-0 text-sm',
  md: 'h-auto py-0 text-sm',
  lg: 'h-auto py-0 text-md',
};

/** Map legacy alias variants to canonical names. */
function resolveVariant(input: ButtonVariantInput): ButtonVariant {
  switch (input) {
    case 'danger':
      return 'destructive';
    case 'default':
      return 'secondary';
    case 'info':
      return 'secondary';
    default:
      return input;
  }
}

/** Map legacy size aliases. */
function resolveSize(input: ButtonSizeInput): ButtonSize {
  if (input === 'xs') return 'sm';
  // Legacy `icon` size maps to sm; callers should also pass variant="icon"
  // for the square-button styling. Pre-Phase-13 sites used size="icon"
  // alone, so we keep them rendering correctly until they migrate.
  if (input === 'icon') return 'sm';
  return input;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    fullWidth = false,
    isLoading = false,
    loadingLabel = 'Loading',
    leftIcon,
    rightIcon,
    type = 'button',
    disabled,
    className,
    children,
    ...rest
  },
  ref,
) {
  const v = resolveVariant(variant);
  const s = resolveSize(size);
  const isIcon = v === 'icon';
  const isLink = v === 'link';

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      className={cn(
        BASE_CLASSES,
        VARIANT_CLASSES[v],
        isIcon ? ICON_SIZE_CLASSES[s] : SIZE_CLASSES[s],
        isLink && LINK_SIZE_OVERRIDE[s],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {/* Width-preserving loading state: keep label rendered but invisible
          while the spinner overlays. Layout doesn't shift when the
          button enters/leaves loading. */}
      {isLoading && (
        <span
          className="absolute inset-0 flex items-center justify-center"
          aria-hidden="true"
        >
          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        </span>
      )}
      <span
        className={cn(
          'inline-flex items-center gap-[inherit]',
          isLoading && 'invisible',
        )}
      >
        {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
        {children}
        {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
      </span>
      {isLoading && <span className="sr-only">{loadingLabel}</span>}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;
